// src/components/pages/auth/SignInPage.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  signInUserWithEmailPassword,
  getFriendlyErrorMessage,
  startGoogleRedirect,
  startAppleRedirect,
  handleRedirectResultOnce,
  finalizeRedirectFallbackIfNeeded,
} from "../../../services/authService";
import {
  Backdrop,
  Box,
  CircularProgress,
  Typography,
  Container,
  Alert,
  Stack,
} from "@mui/material";
import SignInForm from "../../features/auth/SignInForm";
import useUserProfileStore, {
  selectSetUserProfile,
} from "../../../store/useUserProfileStore";
import { clearSignedOutUserStorage } from "../../../store/appStorage";
import { getOrCreateUserProfile } from "../../../services/profileService";

import { db } from "../../../firebase";
import {
  collection,
  getCountFromServer,
  doc,
  getDoc,
  updateDoc,
  query,
  where,
  onSnapshot,
} from "firebase/firestore";
import { getAuth, signOut } from "firebase/auth";
import { useSessionCapabilities } from "../../../hooks/useSessionCapabilities";

const SIGNIN_REDIRECT_KEY = "postAuthRedirectTo";
const REDIRECT_CTX_KEY = "redirectCtx";

const normalizeFromTarget = (fromValue) => {
  if (!fromValue) return null;
  if (typeof fromValue === "string") return fromValue;
  if (typeof fromValue === "object") {
    const pathname = fromValue.pathname || "";
    const search = fromValue.search || "";
    const combined = `${pathname}${search}`;
    return combined || null;
  }
  return null;
};

const SignInPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [capacityNotice, setCapacityNotice] = useState(null);

  const setUserProfile = useUserProfileStore(selectSetUserProfile);

  const [authenticatedUser, setAuthenticatedUser] = useState(null);

  const hasNavigatedRef = useRef(false);
  const processedAuthUidRef = useRef(null);

  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState(null);

  const [userCount, setUserCount] = useState(null);
  const [countLoading, setCountLoading] = useState(true);
  const [countError, setCountError] = useState(null);
  const [redirectBootstrapComplete, setRedirectBootstrapComplete] = useState(false);
  const sessionCapabilities = useSessionCapabilities();

  // Extract next param
  const queryParams = new URLSearchParams(location.search);
  const nextFromUrl = queryParams.get("next");

  const safeGetStorage = (storage, key) => {
    try {
      return storage.getItem(key);
    } catch {
      return null;
    }
  };

  const safeSetStorage = (storage, key, value) => {
    try {
      storage.setItem(key, value);
    } catch (e) {
      console.warn("[signin] failed to set storage key:", key, e);
    }
  };

  const safeRemoveStorage = (storage, key) => {
    try {
      storage.removeItem(key);
    } catch {}
  };

  // Destination preference: ?next, then session key, then location.state, then "/"
  const fromLocation = location.state?.from;
  const fromStatePath = normalizeFromTarget(fromLocation);
  const savedFrom =
    safeGetStorage(localStorage, SIGNIN_REDIRECT_KEY) ||
    safeGetStorage(sessionStorage, SIGNIN_REDIRECT_KEY);
  const initialFromTarget = nextFromUrl || savedFrom || fromStatePath || "/";
  const signupPath = nextFromUrl
    ? `/signup?next=${encodeURIComponent(nextFromUrl)}`
    : "/signup";

  // Admin config
  useEffect(() => {
    setConfigLoading(true);
    const ref = doc(db, "config", "app");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setConfig(snap.exists() ? snap.data() || {} : {});
        setConfigLoading(false);
      },
      (e) => {
        console.error("Error streaming admin config:", e);
        setConfigError("Unable to load admin settings at the moment.");
        setConfig({});
        setConfigLoading(false);
      }
    );
    return () => unsub();
  }, []);

  // Count once
  useEffect(() => {
    const fetchUserCount = async () => {
      setCountLoading(true);
      setCountError(null);
      try {
        const coll = collection(db, "users");
        const q = query(coll, where("hasSignedUpWithINSSA", "==", true));
        const snapshot = await getCountFromServer(q);
        const count = snapshot.data().count || 0;
        setUserCount(count);
      } catch (e) {
        console.error("Error fetching Inssa users count:", e);
        setCountError("Unable to verify capacity at the moment.");
      } finally {
        setCountLoading(false);
      }
    };
    fetchUserCount();
  }, []);

  const alphaEnabled = !!config?.alphaLimitEnabled;
  const limit =
    typeof config?.alphaLimitMaxUsers === "number"
      ? config.alphaLimitMaxUsers
      : undefined;
  const overLimit =
    alphaEnabled &&
    typeof userCount === "number" &&
    typeof limit === "number" &&
    userCount >= Number(limit);

  // Process redirect result on first load
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const rawCtx =
        safeGetStorage(localStorage, REDIRECT_CTX_KEY) ||
        safeGetStorage(sessionStorage, REDIRECT_CTX_KEY);
      const savedCtx = JSON.parse(rawCtx || "{}");
      const redirectedFromHandler = (document.referrer || "").includes("/__/auth/handler");
      const expectedRedirect =
        savedCtx?.pendingRedirect === true ||
        Object.prototype.hasOwnProperty.call(savedCtx, "fromPath") ||
        redirectedFromHandler;
      console.info(
        "[signin] redirect bootstrap start expectedRedirect:",
        expectedRedirect,
        "savedCtx:",
        savedCtx,
        "redirectedFromHandler:",
        redirectedFromHandler
      );

      if (expectedRedirect) {
        setLoading(true);
      }
      try {
        let user = await handleRedirectResultOnce();
        if (!user) {
          user = await finalizeRedirectFallbackIfNeeded(expectedRedirect);
        }
        if (cancelled) return;

        if (user) {
          console.info("[signin] redirect bootstrap authenticated uid:", user?.uid);
          setAuthenticatedUser(user);
        } else {
          console.info("[signin] redirect bootstrap no authenticated user");
        }
      } catch (e) {
        console.error("Redirect result handling failed:", e);
        if (!cancelled) {
          setError("Error finalizing redirect sign-in. Please try again.");
        }
      } finally {
        safeRemoveStorage(sessionStorage, REDIRECT_CTX_KEY);
        safeRemoveStorage(localStorage, REDIRECT_CTX_KEY);
        if (!cancelled) {
          setRedirectBootstrapComplete(true);
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If already signed in and they visit /signin, reuse post-auth flow
  useEffect(() => {
    if (!redirectBootstrapComplete) return;
    const auth = getAuth();
    const current = auth.currentUser;
    if (current) {
      console.info("[signin] current user detected after bootstrap uid:", current?.uid);
      setLoading(true);
      setAuthenticatedUser(current);
    } else {
      console.info("[signin] no current user after bootstrap");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectBootstrapComplete]);

  useEffect(() => {
    if (!authenticatedUser?.uid) {
      processedAuthUidRef.current = null;
    }
  }, [authenticatedUser?.uid]);

  // After auth and profile, decide where to go
  useEffect(() => {
    const redirectAfterProfileCheck = async () => {
      if (hasNavigatedRef.current) return;
      if (!redirectBootstrapComplete) return;
      if (!authenticatedUser) return;
      if (alphaEnabled && (countLoading || configLoading)) return;
      if (processedAuthUidRef.current === authenticatedUser.uid) return;

      processedAuthUidRef.current = authenticatedUser.uid;
      console.info("[signin] post-auth flow start uid:", authenticatedUser?.uid);

      try {
        if (overLimit) {
          const userDocRef = doc(db, "users", authenticatedUser.uid);
          const userDocSnap = await getDoc(userDocRef);

          if (!userDocSnap.exists()) {
            setCapacityNotice(
              config?.alphaLimitReachedMessage ||
                "Sign-ups are currently closed for the alpha."
            );

            const auth = getAuth();
            try {
              await signOut(auth);
            } catch (e) {
              console.warn("Sign out failed:", e);
            }
            clearSignedOutUserStorage();
            setAuthenticatedUser(null);
            setLoading(false);
            console.warn("[signin] blocked by alpha limit for new user");
            return;
          }
        }

        const profile = await getOrCreateUserProfile(authenticatedUser);
        console.info("[signin] profile loaded uid:", authenticatedUser?.uid, "hasProfile:", !!profile);

        try {
          if (!profile?.hasSignedUpWithINSSA) {
            const userDocRef = doc(db, "users", authenticatedUser.uid);
            await updateDoc(userDocRef, { hasSignedUpWithINSSA: true });
            profile.hasSignedUpWithINSSA = true;
          }
        } catch (e) {
          console.warn("Failed to set hasSignedUpWithINSSA flag:", e);
        }

        // must keep this console log style
        setUserProfile(profile);

        const destination = initialFromTarget || "/";
        console.info("[signin] navigating after auth to:", destination);
        hasNavigatedRef.current = true;

        navigate(destination, { replace: true, state: { cameFromAuth: true } });

        // clear saved redirect only after we have navigated
        setTimeout(() => {
          safeRemoveStorage(sessionStorage, REDIRECT_CTX_KEY);
          safeRemoveStorage(localStorage, REDIRECT_CTX_KEY);
          safeRemoveStorage(sessionStorage, SIGNIN_REDIRECT_KEY);
          safeRemoveStorage(localStorage, SIGNIN_REDIRECT_KEY);
        }, 0);
      } catch (e) {
        processedAuthUidRef.current = null;
        console.error("Post-auth profile flow error:", e);
        setError("Error finalizing sign-in. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    redirectAfterProfileCheck();
  }, [
    authenticatedUser,
    redirectBootstrapComplete,
    setUserProfile,
    navigate,
    initialFromTarget,
    countLoading,
    configLoading,
    alphaEnabled,
    overLimit,
    config?.alphaLimitReachedMessage,
  ]);

  // ---------- Button handlers ----------
  const persistDestination = () => {
    const dest = initialFromTarget || "/";
    safeSetStorage(sessionStorage, SIGNIN_REDIRECT_KEY, dest);
    safeSetStorage(localStorage, SIGNIN_REDIRECT_KEY, dest);
    return dest;
  };

  const handleEmailPasswordSignIn = async (email, password) => {
    setLoading(true);
    setError(null);
    setCapacityNotice(null);
    try {
      persistDestination();
      console.info("[signin] email sign-in clicked");
      const user = await signInUserWithEmailPassword(
        email,
        password
      );
      console.info("[signin] email sign-in returned uid:", user?.uid);
      setAuthenticatedUser(user);
    } catch (error) {
      const friendlyMessage = getFriendlyErrorMessage(error.code);
      setError(friendlyMessage);
      console.error("Error signing in with email and password:", error.message);
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    setCapacityNotice(null);
    try {
      const dest = persistDestination();
      console.info("[signin] google sign-in clicked destination:", dest);

      const user = await startGoogleRedirect();

      if (user) {
        console.info("[signin] google popup completed uid:", user?.uid);
        setAuthenticatedUser(user);
      } else {
        console.info("[signin] google redirect started");
        const redirectCtx = JSON.stringify({
          pendingRedirect: true,
          fromPath: dest,
        });
        safeSetStorage(sessionStorage, REDIRECT_CTX_KEY, redirectCtx);
        safeSetStorage(localStorage, REDIRECT_CTX_KEY, redirectCtx);
        console.info("[signin] google redirect context saved:", redirectCtx);
        return;
      }
    } catch (error) {
      const friendlyMessage = getFriendlyErrorMessage(error.code);
      setError(friendlyMessage);
      console.error("Error starting Google sign-in:", error.message);
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setLoading(true);
    setError(null);
    setCapacityNotice(null);
    try {
      const dest = persistDestination();
      console.info("[signin] apple sign-in clicked destination:", dest);

      const user = await startAppleRedirect();

      if (user) {
        console.info("[signin] apple popup completed uid:", user?.uid);
        setAuthenticatedUser(user);
      } else {
        console.info("[signin] apple redirect started");
        const redirectCtx = JSON.stringify({
          pendingRedirect: true,
          fromPath: dest,
        });
        safeSetStorage(sessionStorage, REDIRECT_CTX_KEY, redirectCtx);
        safeSetStorage(localStorage, REDIRECT_CTX_KEY, redirectCtx);
        console.info("[signin] apple redirect context saved:", redirectCtx);
        return;
      }
    } catch (error) {
      const friendlyMessage = getFriendlyErrorMessage(error.code);
      setError(friendlyMessage);
      console.error("Error starting Apple sign-in:", error.message);
      setLoading(false);
    }
  };

  // UI flags
  const showGoogle = (config?.enableGoogleSignIn ?? true) === true;
  const showApple = (config?.enableAppleSignIn ?? true) === true;
  const showEmailPassword = (config?.enableEmailPasswordSignIn ?? true) === true;
  const noAuthMethodsEnabled = !showGoogle && !showApple && !showEmailPassword;

  return (
    <>
      <Backdrop
        sx={(theme) => ({ color: theme.colors.white, zIndex: theme.zIndex.drawer + 1 })}
        open={loading}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <CircularProgress color="inherit" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Signing in...
          </Typography>
        </Box>
      </Backdrop>

        <Container
          maxWidth="xs"
          sx={(theme) => ({
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            minHeight: "100%",
            display: "flex",
            flexDirection: "column",
          })}
      >
        <Stack spacing={2} sx={{ pt: 2 }}>
          {overLimit && (
            <Alert severity="info">
              {config?.alphaLimitReachedMessage ||
                "Sign-ups are currently closed for the alpha."}
            </Alert>
          )}

          {(countError || configError) && (
            <Alert severity="warning">
              We are having trouble verifying capacity right now. You can still try
              signing in, or explore public time capsules without signing in.
            </Alert>
          )}

          {sessionCapabilities.isLimitedSession && (
            <Alert severity="info">
              Private browsing or browser privacy settings may prevent sign-in from staying saved. For shared capsules, a regular browser tab gives the most reliable access.
            </Alert>
          )}

          {noAuthMethodsEnabled && (
            <Alert severity="error">
              Registration and sign-in are temporarily suspended. Please check back soon.
            </Alert>
          )}

          {capacityNotice && <Alert severity="error">{capacityNotice}</Alert>}
        </Stack>

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {!noAuthMethodsEnabled && (
            <SignInForm
              handleEmailPasswordSignIn={handleEmailPasswordSignIn}
              handleGoogleSignIn={handleGoogleSignIn}
              handleAppleSignIn={handleAppleSignIn}
              loading={loading}
              error={error}
              registrationClosed={overLimit}
              showGoogle={showGoogle}
              showApple={showApple}
              showEmailPassword={showEmailPassword}
              signupPath={signupPath}
            />
          )}
        </Box>
      </Container>
    </>
  );
};

export default SignInPage;
